import i18n from "@/i18n";
import { getCurrentLocale } from "@/translations/functions/locale";

/**
 * Formats number with thousand seperator
 * @param n
 * @returns formated string
 */
export const formatNumber = (n: number | string) => {
  return Number(n).toLocaleString(i18n.language);
};

/**
 * Rounds/formats number to specified decimal places
 * @param number
 * @param decimalPlaces
 * @returns rounded number with specific number of decimal places
 */
export const formatNumberWithDecimalPlaces = (
  number: number,
  decimalPlaces: number,
) =>
  Math.round((number + Number.EPSILON) * 10 ** decimalPlaces) /
  10 ** decimalPlaces;

export type TFormatNumberSuffix = "" | " %" | " kg";

export const roundNumberForTable = (
  number: number,
  numDecimals: number = 1,
): number => {
  const shouldUseDecimals = numDecimals > 0;
  return formatNumberWithDecimalPlaces(
    number,
    shouldUseDecimals ? numDecimals : 0,
  );
};

export function getIntlLocale() {
  if (navigator.languages !== undefined) {
    return navigator.languages[0];
  }
  return navigator.language;
}

type Options = Partial<{
  suffix: TFormatNumberSuffix;
  numDecimals: number;
  maxLength: number;
  locale: string;
  fixedDecimals: boolean;
}>;

/**
 * @param number
 * @param suffix
 * @param numDecimals
 * @param maxLength max length of the number when displayed as a string. Above this length the number will be displayed in exponential notation
 * @returns formated string
 */
export const formatNumberForTable = (
  number: number | string | undefined,
  options?: Options,
) => {
  const endsWithPeriod = typeof number === "string" && number.slice(-1) === ".";
  const _number = endsWithPeriod ? Number(number.slice(0, -1)) : Number(number);
  const {
    maxLength = 8,
    numDecimals = 1,
    suffix = "",
    locale = getCurrentLocale(),
    fixedDecimals = false,
  } = options || {};

  const shouldIncludeEndingPeriod = endsWithPeriod;
  const localeDecimalSeparator = new Intl.NumberFormat(locale)
    .format(1.1)
    .charAt(1);

  if (isNaN(_number)) return "0";

  const parsedNumber = roundNumberForTable(_number, numDecimals);

  const shouldBeExponential = parsedNumber.toString().length > maxLength;
  const formatedValue = parsedNumber.toLocaleString(locale, {
    notation: shouldBeExponential ? "scientific" : "standard",
    minimumFractionDigits: shouldBeExponential
      ? 1
      : fixedDecimals
        ? numDecimals
        : undefined,
    maximumFractionDigits: shouldBeExponential ? 1 : undefined,
  });
  const isPercentValue = suffix.includes(" %");

  if (isPercentValue && numDecimals) {
    const isZeroOrFull = !_number || _number === 100;
    const fullNumber = Number(_number).toLocaleString(locale, {
      minimumFractionDigits: isZeroOrFull ? 0 : 1,
      maximumFractionDigits: isZeroOrFull ? 0 : 1,
    });
    const maxLengthAccountingForNegative =
      _number < 0 ? maxLength + 1 : maxLength;
    const shouldBeExponential =
      fullNumber.length > maxLengthAccountingForNegative;
    return (
      _number.toLocaleString(locale, {
        minimumFractionDigits: isZeroOrFull ? 0 : 1,
        maximumFractionDigits: isZeroOrFull ? 0 : 1,
        notation: shouldBeExponential ? "scientific" : "standard",
      }) + suffix
    );
  }

  return (
    formatedValue +
    suffix +
    (shouldIncludeEndingPeriod ? localeDecimalSeparator : "")
  );
};

export const removeMoreThanTwoDecimals = (num: number) =>
  Math.round(num * 100) / 100;
export const removeDecimals = (num: number) => Number.parseInt(num.toFixed(0));

const DECIMALS_DEFAULT = 1;

export const clampWeight = (
  w: number | string,
  decimals = DECIMALS_DEFAULT,
) => {
  const weight = Number(w);
  if (weight === 0) return 0;

  const minWeight = 1 / Math.pow(10, decimals);
  const rounded = parseFloat(weight.toFixed(decimals));

  return weight > 0
    ? Math.max(rounded, minWeight)
    : Math.min(rounded, -minWeight);
};

export const formatWeight = (weight: number | string, options?: Options) => {
  const roundedWeight = clampWeight(Number(weight), options?.numDecimals);
  return formatNumberForTable(roundedWeight, {
    numDecimals: options?.numDecimals ?? DECIMALS_DEFAULT,
    ...options,
  });
};

export const formatCost = (value: number | string, currencyKey: string) => {
  const locale = getIntlLocale();
  if (!value) return "";
  const formattedValue = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyKey,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
  return formattedValue;
};
